// src/pages/cliente.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { fetchClienteById, updateCliente, Cliente } from "../api/clientesApi";
import {
  fetchClasificaciones,
  Clasificacion,
} from "../api/optionsApi";

import { provinciasOptions as lugaresOptions } from "../constants/provincias"; // ← NUEVO
import "../css/cliente.css";
import LoadingLottie from "../components/LoadingLottie";
import TreeSelectCpv from "../components/treeselect/TreeSelectCpv";

// Importación de react-toastify
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { paisesOptions } from "../constants/paises";

function ClienteEditar() {
  const { id_usuario } = useParams<{ id_usuario: string }>();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // ------------------------------- estados editables -------------------------------
  const [telefono, setTelefono] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [cif, setCif] = useState("");
  const [direccion, setDireccion] = useState("");
  const [actividad, setActividad] = useState("");
  const [cp, setCp] = useState("");
  const [poblacion, setPoblacion] = useState("");
  const [cuenta, setCuenta] = useState("");
  const [iban, setIban] = useState("");
  const [codsClasificaciones, setCodsClasificaciones] = useState("");
  const [codsCpv, setCodsCpv] = useState("");
  const [codsCpvIgnorar, setCodsCpvIgnorar] = useState("");
  const [lugares, setLugares] = useState("");
  const [paises, setPaises] = useState("");
  const [importeMin, setImporteMin] = useState("");
  const [importeMax, setImporteMax] = useState("");
  const [fBaja, setFBaja] = useState("");
  const [clasificacionesReconocidas, setClasificacionesReconocidas] =
    useState("");
  const [obs, setObs] = useState("");

  // Campos favoritos
  const [fechaConstitucion, setFechaConstitucion] = useState("");
  const [habilitacionProfesional, setHabilitacionProfesional] = useState("");
  const [cifraAnualNegocio, setCifraAnualNegocio] = useState("");
  const [certificadosCBE, setCertificadosCBE] = useState("");
  const [planIgualdad, setPlanIgualdad] = useState("");
  const [numeroEmpleados, setNumeroEmpleados] = useState("");
  const [otrosContactos, setOtrosContactos] = useState("");

  // Keywords
  const [kwAnadir, setKwAnadir] = useState("");
  const [kwEliminar, setKwEliminar] = useState("");

  // Opciones de clasificaciones
  const [clasificacionesOptions, setClasificacionesOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // ------------------------------- efectos -------------------------------
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchClasificaciones();
        setClasificacionesOptions(
          data.map((item: Clasificacion) => ({
            value: item.cod_clasificacion,
            label: `${item.cod_clasificacion} - ${item.clasificacion}`,
          }))
        );
      } catch (err) {
        console.error("Error al cargar clasificaciones:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!id_usuario) return;
        const data = await fetchClienteById(id_usuario);

        setCliente(data);
        setTelefono(data.telefono ?? "");
        setEmpresa(data.empresa ?? "");
        setCif(data.cif ?? "");
        setDireccion(data.direccion ?? "");
        setActividad(data.actividad ?? "");
        setCp(data.cp ?? "");
        setPoblacion(data.poblacion ?? "");
        setCuenta(data.cuenta ?? "");
        setIban(data.iban ?? "");
        setCodsClasificaciones(data.cods_clasificaciones ?? "");
        setCodsCpv(data.cods_cpv ?? "");
        setCodsCpvIgnorar(data.cods_cpv_ignorar ?? "");
        setLugares(data.lugares ?? "");
        setPaises(data.paises ?? "");
        setImporteMin(data.importe_min ?? "");
        setImporteMax(data.importe_max ?? "");
        setFBaja(data.f_baja ? data.f_baja.split("T")[0] : "");
        setClasificacionesReconocidas(data.clasificaciones_reconocidas ?? "");
        setObs(data.obs ?? "");

        if (data.fecha_constitucion) {
          const [month, year] = data.fecha_constitucion.split("/");
          setFechaConstitucion(`${year}-${month}`);
        }
        setHabilitacionProfesional(data.habilitacion_profesional ?? "");
        setCifraAnualNegocio(data.cifra_anual_negocio ?? "");
        setCertificadosCBE(data.certificados_cbe ?? "");
        setPlanIgualdad(data.plan_igualdad ?? "");
        setNumeroEmpleados(
          data.numero_empleados !== null ? data.numero_empleados.toString() : ""
        );
        setOtrosContactos(data.otros_contactos ?? "");
        setKwAnadir(data.kw_anadir ?? "");
        setKwEliminar(data.kw_eliminar ?? "");
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Error al cargar el cliente."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id_usuario]);

  // ------------------------------- envío -------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!id_usuario) return;

      const formattedFecha = fechaConstitucion
        ? `${fechaConstitucion.split("-")[1]}/${
            fechaConstitucion.split("-")[0]
          }`
        : "";

      const updates: Partial<Cliente> = {
        telefono,
        empresa,
        cif,
        direccion,
        actividad,
        cp,
        poblacion,
        cuenta,
        iban,
        cods_clasificaciones: codsClasificaciones,
        cods_cpv: codsCpv,
        cods_cpv_ignorar: codsCpvIgnorar,
        lugares,
        paises,
        importe_min: importeMin,
        importe_max: importeMax,
        f_baja: fBaja || null,
        clasificaciones_reconocidas: clasificacionesReconocidas,
        obs,
        fecha_constitucion: formattedFecha,
        habilitacion_profesional: habilitacionProfesional,
        cifra_anual_negocio: cifraAnualNegocio,
        certificados_cbe: certificadosCBE,
        plan_igualdad: planIgualdad,
        numero_empleados: numeroEmpleados,
        otros_contactos: otrosContactos,
        kw_anadir: kwAnadir,
        kw_eliminar: kwEliminar,
      };

      await updateCliente(id_usuario, updates);

      toast.success("¡Datos guardados correctamente!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al actualizar el cliente."
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------- render -------------------------------
  if (loading) return <LoadingLottie />;
  if (error) return <p className="cliente-error">Error: {error}</p>;
  if (!cliente)
    return <p className="cliente-no-data">Cliente no encontrado.</p>;

  return (
    <div className="cliente-container">
      <h2 className="cliente-title">Editar Cliente</h2>
      <form onSubmit={handleSubmit} className="cliente-form">
        {/* INFORMACIÓN EMPRESA */}
        <details className="cliente-details">
          <summary className="cliente-summary">INFORMACIÓN EMPRESA</summary>
          <div className="cliente-form-group">
            <label>ID Usuario:</label>
            <input
              type="text"
              value={cliente.id_usuario}
              disabled
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Nombre:</label>
            <input
              type="text"
              value={cliente.nombre ?? ""}
              disabled
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Email:</label>
            <input
              type="text"
              value={cliente.email ?? ""}
              disabled
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Fecha de Alta:</label>
            <input
              type="date"
              value={cliente.f_alta ? cliente.f_alta.split("T")[0] : ""}
              disabled
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Teléfono:</label>
            <input
              type="text"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Empresa:</label>
            <input
              type="text"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>CIF:</label>
            <input
              type="text"
              value={cif}
              onChange={(e) => setCif(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Dirección:</label>
            <textarea
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              className="cliente-textarea"
            />
          </div>
          <div className="cliente-form-group">
            <label>Actividad:</label>
            <textarea
              value={actividad}
              onChange={(e) => setActividad(e.target.value)}
              className="cliente-textarea"
            />
          </div>
          <div className="cliente-form-group">
            <label>CP:</label>
            <input
              type="text"
              value={cp}
              onChange={(e) => setCp(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Población:</label>
            <input
              type="text"
              value={poblacion}
              onChange={(e) => setPoblacion(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Cuenta:</label>
            <textarea
              value={cuenta}
              onChange={(e) => setCuenta(e.target.value)}
              className="cliente-textarea"
            />
          </div>
          <div className="cliente-form-group">
            <label>IBAN:</label>
            <input
              type="text"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              className="cliente-input"
            />
          </div>
        </details>

        {/* CARACTERÍSTICAS EMPRESA */}
        <details className="cliente-details">
          <summary className="cliente-summary">CARACTERÍSTICAS EMPRESA</summary>
          <div className="cliente-form-group">
            <label>Fecha de Baja:</label>
            <input
              type="date"
              value={fBaja}
              onChange={(e) => setFBaja(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Clasificaciones Reconocidas:</label>
            <textarea
              value={clasificacionesReconocidas}
              onChange={(e) => setClasificacionesReconocidas(e.target.value)}
              className="cliente-textarea"
            />
          </div>
          <div className="cliente-form-group">
            <label>Fecha de constitución (MM/AAAA):</label>
            <input
              type="month"
              value={fechaConstitucion}
              onChange={(e) => setFechaConstitucion(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Habilitación profesional:</label>
            <textarea
              value={habilitacionProfesional}
              onChange={(e) => setHabilitacionProfesional(e.target.value)}
              className="cliente-textarea"
            />
          </div>
          <div className="cliente-form-group">
            <label>
              Cifra anual de negocio (en el mejor de los 3 últimos años):
            </label>
            <input
              type="text"
              value={cifraAnualNegocio}
              onChange={(e) => setCifraAnualNegocio(e.target.value)}
              className="cliente-input"
              placeholder="AAAA/-€"
            />
          </div>
          <div className="cliente-form-group">
            <label>Certificados de buena ejecución (CBE):</label>
            <select
              value={certificadosCBE}
              onChange={(e) => setCertificadosCBE(e.target.value)}
              className="cliente-input"
            >
              <option value="">Seleccionar</option>
              <option value="SÍ">SÍ</option>
              <option value="NO">NO</option>
            </select>
          </div>
          <div className="cliente-form-group">
            <label>Plan de Igualdad (SI/NO):</label>
            <select
              value={planIgualdad}
              onChange={(e) => setPlanIgualdad(e.target.value)}
              className="cliente-input"
            >
              <option value="">Seleccionar</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>
          <div className="cliente-form-group">
            <label>N.º Empleados:</label>
            <input
              type="number"
              value={numeroEmpleados}
              onChange={(e) => setNumeroEmpleados(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Otros contactos:</label>
            <textarea
              value={otrosContactos}
              onChange={(e) => setOtrosContactos(e.target.value)}
              className="cliente-textarea"
            />
          </div>
          <div className="cliente-form-group">
            <label>Observaciones:</label>
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              className="cliente-textarea"
            />
          </div>
        </details>

        {/* FILTROS */}
        <details className="cliente-details">
          <summary className="cliente-summary">FILTROS</summary>
          <div className="cliente-form-group">
            <label>Códs. Clasificaciones:</label>
            <Select
              isMulti
              options={clasificacionesOptions}
              getOptionLabel={(option) => option.value}
              getOptionValue={(option) => option.value}
              value={
                codsClasificaciones
                  ? codsClasificaciones
                      .split(",")
                      .map((val) => ({ value: val.trim(), label: "" }))
                  : []
              }
              onChange={(selected) => {
                setCodsClasificaciones(selected.map((s) => s.value).join(","));
              }}
              placeholder="Seleccione clasificaciones..."
            />
          </div>
          <div className="cliente-form-group">
            <label>Códs. CPV:</label>
            <TreeSelectCpv
              value={codsCpv}
              onChange={setCodsCpv}
              variant="include"
              label="CPVs incluidos"
            />
          </div>
          <div className="cliente-form-group">
            <label>Códs. CPV Ignorar:</label>
            <TreeSelectCpv
              value={codsCpvIgnorar}
              onChange={setCodsCpvIgnorar}
              variant="exclude"
              label="CPVs a ignorar"
            />
          </div>
          <div className="cliente-form-group">
            <label>Lugares:</label>
            <Select
              isMulti
              options={lugaresOptions}
              value={
                lugares
                  ? lugares
                      .split(",")
                      .map((i) => ({ value: i.trim(), label: i.trim() }))
                  : []
              }
              onChange={(selected) =>
                setLugares(selected.map((s) => s.value).join(","))
              }
              placeholder="Seleccione provincias..."
            />
          </div>
          <div className="cliente-form-group">
            <label>Países:</label>
            <Select
              isMulti
              options={paisesOptions}
              value={
                paises
                  ? paises
                      .split(",")
                      .map((i) => ({ value: i.trim(), label: i.trim() }))
                  : []
              }
              onChange={(selected) =>
                setPaises(selected.map((s) => s.value).join(","))
              }
              placeholder="Seleccione países..."
            />
          </div>
          <div className="cliente-form-group">
            <label>Importe Mínimo (€):</label>
            <input
              type="number"
              value={importeMin}
              onChange={(e) => setImporteMin(e.target.value)}
              className="cliente-input"
            />
          </div>
          <div className="cliente-form-group">
            <label>Importe Máximo (€):</label>
            <input
              type="number"
              value={importeMax}
              onChange={(e) => setImporteMax(e.target.value)}
              className="cliente-input"
            />
          </div>

          {/* KEYWORDS */}
          <div className="cliente-form-group">
            <label>Keywords para añadir:</label>
            <p className="cliente-info">
              Añade palabras clave para buscar en los títulos de los
              concursos...
            </p>
            <CreatableSelect
              isMulti
              placeholder="Escriba y presione Enter..."
              value={
                kwAnadir
                  ? kwAnadir
                      .split(",")
                      .map((s) => ({ value: s.trim(), label: s.trim() }))
                  : []
              }
              onChange={(selected) =>
                setKwAnadir(selected.map((s) => s.value).join(","))
              }
            />
          </div>
          <div className="cliente-form-group">
            <label>Keywords para eliminar:</label>
            <p className="cliente-info">
              Excluye concursos que no contengan alguna de estas palabras...
            </p>
            <CreatableSelect
              isMulti
              placeholder="Escriba y presione Enter..."
              value={
                kwEliminar
                  ? kwEliminar
                      .split(",")
                      .map((s) => ({ value: s.trim(), label: s.trim() }))
                  : []
              }
              onChange={(selected) =>
                setKwEliminar(selected.map((s) => s.value).join(","))
              }
            />
          </div>
        </details>

        {error && <p className="cliente-error-message">{error}</p>}

        <div className="cliente-form-actions">
          <button
            type="submit"
            className="cliente-btn guardar"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        style={{ marginTop: "125px" }}
      />
    </div>
  );
}

export default ClienteEditar;
